/*
  @stl/core: the shared behaviour for every custom Say The Label section.
  Slice 0b, frozen after review. Only the conductor changes this file.

  Registered in the importmap (snippets/scripts.liquid) as "@stl/core" and loaded on every page by
  snippets/stl-head.liquid. Import it from any module script:

    import { toast, openDrawer, wishlist, cart, money, style } from '@stl/core';

  Or use none of it from JavaScript and drive it from markup:

    data-stl-open="<overlay id>"        click opens that overlay
    data-stl-close                      click closes the overlay it sits in
    data-stl-wish="<product handle>"    click saves or removes the product; state is kept in step
    data-stl-add="<variant id>"         click adds the variant to the cart (data-stl-qty optional)
    data-stl-toast="<message>"          click shows the message

  An overlay is any element with an id and data-stl-overlay="<kind>", where kind is one of
  drawer, modal, menu, popup, player. State is one class, stl-is-on, and this file is the
  only thing that writes it on an overlay. Every overlay gets role="dialog", aria-modal, a focus
  trap, focus return to whatever opened it, and Escape. The mock has none of those.

  SEARCH IS NOT AN OVERLAY. It is the theme's own dialog, <dialog-component id="search-modal">,
  which layout/theme.liquid renders on every page and the global slice restyles. A search button
  opens it with the theme's own attribute, from anywhere in the document:
    <button type="button" on:click="#search-modal/showDialog" aria-haspopup="dialog">

  THEME DIALOGS AND OVERLAYS NEVER OVERLAP. A theme <dialog> (cart drawer, search, quick add) is in
  the browser's top layer and makes the rest of the page inert, so an overlay opened beside one
  would sit underneath it, unseen, and appear when it closed. Two rules keep that from happening:
  a data-stl-open click inside a theme dialog closes the dialog first and then opens the overlay;
  openOverlay() called from JavaScript while one is open returns null and warns (call
  closeThemeDialogs() first). And when a theme dialog opens, any open drawer or menu overlay closes.

  Nothing here throws on a page that lacks an element. Storage is wrapped in try/catch because
  private browsing throws.
*/

import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

const ON = 'stl-is-on';

export const EVENTS = Object.freeze({
  wish: 'stl:wish:change',
  cart: 'stl:cart:change',
  overlay: 'stl:overlay:change',
});

export const KEYS = Object.freeze({
  wish: 'stl_wish',
  sizeTab: 'stl_size_tab',
  pop: 'stl_pop',
  signed: 'stl_signed',
  teaserOff: 'stl_teaser_off',
});

export const IDS = Object.freeze({
  bagN: 'stl-bagN',
  wishN: 'stl-wishN',
  stickyAtc: 'stl-stickyAtc',
  menu: 'stl-menu',
  pop: 'stl-pop',
  scrim: 'stl-scrim',
  toast: 'stl-toast',
  modal: 'stl-modal',
  drawer: 'stl-drawer',
});

/* ------------------------------------------------------------------ small helpers */

const byId = (id) => (id ? document.getElementById(String(id).replace(/^#/, '')) : null);

/** Runs fn once the DOM is parsed. Safe to call at any time. */
export function onReady(fn) {
  if (typeof fn !== 'function') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => fn(), { once: true });
  } else {
    fn();
  }
}

/**
 * Event delegation. handler(event, matchedElement). Returns a function that removes the listener.
 * delegate(document, 'click', '[data-x]', (e, el) => {})
 */
export function delegate(root, type, selector, handler, options) {
  const target = root || document;
  const listener = (event) => {
    const start = event.target instanceof Element ? event.target : null;
    const el = start ? start.closest(selector) : null;
    if (el && (target === document || target.contains(el))) handler(event, el);
  };
  target.addEventListener(type, listener, options);
  return () => target.removeEventListener(type, listener, options);
}

/** Storage that never throws. area is 'local' or 'session'. Values are JSON. */
export const storage = {
  get(key, fallback = null, area = 'local') {
    try {
      const raw = (area === 'session' ? sessionStorage : localStorage).getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  },
  set(key, value, area = 'local') {
    try {
      (area === 'session' ? sessionStorage : localStorage).setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  },
  remove(key, area = 'local') {
    try {
      (area === 'session' ? sessionStorage : localStorage).removeItem(key);
    } catch (e) {
      /* nothing to do */
    }
  },
};

function fire(name, detail) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

/* ------------------------------------------------------------------ config from Liquid */

let configCache = null;

/** The values snippets/stl-head.liquid printed for this page (market, currency, routes, strings). */
export function config() {
  if (configCache) return configCache;
  let parsed = {};
  try {
    const el = document.getElementById('stl-core-config');
    if (el) parsed = JSON.parse(el.textContent || '{}');
  } catch (e) {
    parsed = {};
  }
  configCache = parsed;
  return configCache;
}

function route(name, fallback) {
  const fromStl = config().routes && config().routes[name];
  if (fromStl) return fromStl;
  try {
    /* global Theme */
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes[name]) return Theme.routes[name];
  } catch (e) {
    /* Theme is not defined on this layout */
  }
  return fallback;
}

function str(name, fallback) {
  const strings = config().strings || {};
  return strings[name] || fallback;
}

/* ------------------------------------------------------------------ money */

/**
 * Formats cents in the active market's currency and format, the same way Liquid's money filter
 * does. money(18000) is "$180.00" in Australia and the converted USD string on /en-us.
 */
export function money(cents) {
  const value = Math.round(Number(cents));
  if (!Number.isFinite(value)) return '';
  const cfg = config();
  const format = String(cfg.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
  const currency = cfg.currency || 'AUD';
  /* the theme's formatter and Liquid's money filter both put a minus AFTER the symbol ("$-5.00",
     measured on the copy in AUD, USD and GBP). A discount line wants "-$5.00", so format the size
     of the amount and put the sign in front here. For a negative amount core therefore does NOT
     print what Liquid prints, on purpose. */
  const sign = value < 0 ? '-' : '';
  const size = Math.abs(value);
  try {
    return sign + formatMoney(size, format, currency);
  } catch (e) {
    return sign + (size / 100).toFixed(2);
  }
}

/* ------------------------------------------------------------------ toast */

let toastTimer = 0;

function toastEl() {
  let el = byId(IDS.toast);
  if (!el && document.body) {
    el = document.createElement('div');
    el.id = IDS.toast;
    el.className = 'stl-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  return el;
}

/* Where the toast lives when nothing is in the way (its place in sections/stl-chrome.liquid). */
let toastHome = null;

/** The modal <dialog> on top of the page right now (the theme's cart drawer, search, quick add), or null. */
function topModalDialog() {
  let found = null;
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    try {
      if (dialog.matches(':modal')) found = dialog;
    } catch (e) {
      /* a browser without :modal: leave the toast where it is */
    }
  });
  return found;
}

/**
 * A modal <dialog> sits in the browser's top layer, above any z-index, and makes everything
 * outside itself inert, so a toast left in the page is both covered and silent. While one is open
 * the toast is moved INSIDE it (seen, and announced), and moved home again afterwards.
 */
function placeToast(el) {
  if (!el) return;
  if (!toastHome && el.parentNode && !(el.parentNode instanceof HTMLDialogElement)) {
    toastHome = { parent: el.parentNode, next: el.nextSibling };
  }
  const dialog = el.classList.contains(ON) ? topModalDialog() : null;
  if (dialog) {
    if (el.parentNode !== dialog) dialog.appendChild(el);
    return;
  }
  if (el.parentNode instanceof HTMLDialogElement) {
    const home = toastHome && document.contains(toastHome.parent) ? toastHome : { parent: document.body, next: null };
    home.parent.insertBefore(el, home.next && home.next.parentNode === home.parent ? home.next : null);
  }
}

/** One message at a time, 2.6 seconds, text only (never markup). */
export function toast(message, options = {}) {
  const el = toastEl();
  if (!el || message === undefined || message === null) return;
  const duration = Number(options.duration) > 0 ? Number(options.duration) : 2600;
  el.classList.add(ON);
  placeToast(el);
  /* set the text one frame after the region is visible so a screen reader announces it */
  requestAnimationFrame(() => {
    el.textContent = String(message);
  });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    el.classList.remove(ON);
    el.textContent = '';
    placeToast(el);
  }, duration);
}

/* ------------------------------------------------------------------ focus trap */

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  'video[controls]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (el.closest('[hidden], [inert]')) return false;
    const style = getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return el.getClientRects().length > 0;
  });
}

const traps = [];

function onTrapKey(event) {
  if (event.key !== 'Tab' || !traps.length) return;
  /* only the newest trap acts. With a drawer open under a modal, both used to run on every Tab
     and the older one dragged focus back to its own first element. */
  const container = traps[traps.length - 1];
  /* a modal <dialog> opened over us (the theme's cart drawer) traps focus itself */
  const dialog = topModalDialog();
  if (dialog && !container.contains(dialog)) return;
  const items = focusables(container);
  if (!items.length) {
    event.preventDefault();
    container.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (!container.contains(active)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && (active === first || active === container)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Keeps Tab inside container. Returns release(). Used by every overlay; exported for a slice that
 * builds something overlay-like outside the registry. Traps stack: only the newest one acts, and
 * releasing it hands Tab back to the one underneath.
 */
export function trapFocus(container) {
  if (!(container instanceof Element)) return () => {};
  if (!traps.length) document.addEventListener('keydown', onTrapKey, true);
  traps.push(container);
  return () => {
    const index = traps.lastIndexOf(container);
    if (index !== -1) traps.splice(index, 1);
    if (!traps.length) document.removeEventListener('keydown', onTrapKey, true);
  };
}

/* ------------------------------------------------------------------ the theme's own dialogs */

/**
 * Closes every open theme dialog (cart drawer, search, quick add) the theme's own way and resolves
 * when they are shut. It has to be the component's closeDialog(): that is what gives the page its
 * scroll position back and tidies the cart drawer's history entry; a bare dialog.close() is the
 * fallback for a <dialog> with no component round it. Resolves true when nothing is left open.
 */
export async function closeThemeDialogs() {
  const open = Array.from(document.querySelectorAll('dialog[open]')).reverse();
  for (const dialog of open) {
    let owner = dialog.parentElement;
    while (owner && typeof owner.closeDialog !== 'function') owner = owner.parentElement;
    try {
      if (owner) await owner.closeDialog();
      if (dialog.open) dialog.close();
    } catch (e) {
      if (dialog.open) dialog.close();
    }
  }
  return !nativeDialogOpen();
}

/* ------------------------------------------------------------------ overlay registry */

/** open overlays, oldest first: { id, el, kind, returnTo, release } */
const stack = [];
let lastBusy = null;
const SCRIM_KINDS = ['drawer'];
const EXCLUSIVE_KINDS = ['drawer'];
/* opening a drawer also closes these, as the mock does (its phone menu closes before the wishlist opens) */
const CLOSED_BY_A_DRAWER = ['drawer', 'menu'];

function kindOf(el) {
  return (el && el.getAttribute('data-stl-overlay')) || 'drawer';
}

function wantsScrim(el) {
  if (el.hasAttribute('data-stl-scrim')) return el.getAttribute('data-stl-scrim') !== 'false';
  return SCRIM_KINDS.includes(kindOf(el));
}

function scrimEl() {
  let el = byId(IDS.scrim);
  if (!el && document.body) {
    el = document.createElement('div');
    el.id = IDS.scrim;
    el.className = 'stl-scrim';
    document.body.appendChild(el);
  }
  return el;
}

function paintScrim() {
  const el = scrimEl();
  if (!el) return;
  el.classList.toggle(ON, stack.some((entry) => wantsScrim(entry.el)));
}

function prepare(el) {
  if (!el.hasAttribute('role')) el.setAttribute('role', 'dialog');
  if (!el.hasAttribute('aria-modal')) el.setAttribute('aria-modal', 'true');
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
}

function nativeDialogOpen() {
  return !!document.querySelector('dialog[open]');
}

function classOn(id) {
  const el = byId(id);
  return !!(el && el.classList.contains(ON));
}

/**
 * True when something is covering the page or the corners: an open drawer, menu, pop-up or player,
 * the phone sticky add-to-bag bar, or a theme dialog (cart drawer, search). A size-guide style
 * modal does not count, exactly as in the mock. The sign-up tab and the Questions? pill hide
 * while this is true (the mock's teaserSync).
 */
export function isBusy() {
  if (stack.some((entry) => entry.kind !== 'modal')) return true;
  if (classOn(IDS.stickyAtc) || classOn(IDS.menu) || classOn(IDS.pop)) return true;
  if (document.querySelector('.stl-drawer.' + ON + ', .stl-cart.' + ON + ', [data-stl-busy].' + ON)) return true;
  return nativeDialogOpen();
}

function announce(entry, open) {
  const busy = isBusy();
  lastBusy = busy;
  document.documentElement.toggleAttribute('data-stl-busy', busy);
  fire(EVENTS.overlay, {
    id: entry ? entry.id : null,
    kind: entry ? entry.kind : null,
    open: !!open,
    openIds: stack.map((item) => item.id),
    busy,
  });
}

/**
 * Re-reads the page and fires stl:overlay:change if "busy" changed. The mock's teaserSync().
 * Call it after toggling stl-is-on yourself on #stl-stickyAtc, #stl-menu or any [data-stl-busy]
 * element. (Those ids are also watched, so this is a belt as well as braces.)
 */
export function syncOverlays() {
  const busy = isBusy();
  if (busy === lastBusy) return busy;
  announce(null, false);
  return busy;
}

function fill(el, options) {
  if (!options) return;
  if (options.title !== undefined) {
    const title = el.querySelector('[data-stl-overlay-title]');
    if (title) title.textContent = String(options.title);
  }
  const body = el.querySelector('[data-stl-overlay-body]');
  if (!body) return;
  if (options.node instanceof Node) {
    body.replaceChildren(options.node);
  } else if (options.html !== undefined) {
    body.innerHTML = String(options.html);
  }
}

/**
 * Opens any overlay by id. options: { title, html, node } fill the shared shells
 * ([data-stl-overlay-title] and [data-stl-overlay-body]); { focus } is a selector or element to
 * focus instead of the first focusable thing; { returnTo } overrides the element focus goes back
 * to. Returns the element, or null when there is no such overlay on this page, or when a theme
 * dialog is open and the overlay is not inside it (see closeThemeDialogs).
 */
export function openOverlay(id, options = {}) {
  const el = byId(id);
  if (!el) return null;
  /* A modal <dialog> is in the top layer and the page outside it is inert: an overlay opened now
     would sit underneath, unseen, and be revealed when the dialog closed. */
  const dialog = topModalDialog();
  if (dialog && !dialog.contains(el)) {
    console.warn('[stl-core] "' + el.id + '" was not opened: a theme dialog is open. Call closeThemeDialogs() first, or use data-stl-open, which does.');
    return null;
  }
  const kind = kindOf(el);
  const existing = stack.find((entry) => entry.el === el);
  if (existing) {
    fill(el, options);
    return el;
  }
  let returnTo =
    options.returnTo instanceof Element
      ? options.returnTo
      : document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null;

  if (EXCLUSIVE_KINDS.includes(kind)) {
    stack
      .filter((entry) => CLOSED_BY_A_DRAWER.includes(entry.kind))
      .forEach((entry) => {
        /* the opener sits inside something that is about to close and go inert (the wishlist link
           in the phone menu): focus will go back to whatever opened THAT instead */
        if (returnTo && entry.el.contains(returnTo)) returnTo = entry.returnTo;
        closeEntry(entry, { silent: true, keepFocus: true });
      });
  }

  fill(el, options);
  prepare(el);
  el.removeAttribute('inert');
  el.classList.add(ON);
  const entry = { id: el.id, el, kind, returnTo, release: trapFocus(el) };
  stack.push(entry);
  paintScrim();

  const wanted =
    options.focus instanceof Element
      ? options.focus
      : typeof options.focus === 'string'
      ? el.querySelector(options.focus)
      : el.querySelector('[data-stl-autofocus]');
  /* wait a frame: a drawer is visibility:hidden until the class lands, and hidden things cannot take focus */
  requestAnimationFrame(() => {
    if (!stack.includes(entry)) return;
    const target = wanted || focusables(el)[0] || el;
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      /* an element that cannot be focused is not worth throwing over */
    }
  });

  announce(entry, true);
  return el;
}

function closeEntry(entry, options = {}) {
  const index = stack.indexOf(entry);
  if (index === -1) return;
  stack.splice(index, 1);
  entry.release();
  entry.el.classList.remove(ON);
  entry.el.setAttribute('inert', '');
  paintScrim();
  if (!options.keepFocus && entry.returnTo && document.contains(entry.returnTo)) {
    try {
      entry.returnTo.focus({ preventScroll: true });
    } catch (e) {
      /* the trigger has gone */
    }
  }
  if (!options.silent) announce(entry, false);
}

/** Closes one overlay by id, or the top one when no id is given. */
export function closeOverlay(id, options = {}) {
  const entry = id ? stack.find((item) => item.id === String(id).replace(/^#/, '')) : stack[stack.length - 1];
  if (entry) closeEntry(entry, options);
}

/** Opens a drawer. Only one drawer is ever open: every other drawer closes first. */
export function openDrawer(id, options) {
  return openOverlay(id || IDS.drawer, options);
}

/** Closes every open drawer and clears the scrim. */
export function closeDrawers(options = {}) {
  const drawers = stack.filter((entry) => EXCLUSIVE_KINDS.includes(entry.kind));
  drawers.forEach((entry, i) => closeEntry(entry, { ...options, silent: true, keepFocus: options.keepFocus || i < drawers.length - 1 }));
  if (drawers.length && !options.silent) announce(drawers[drawers.length - 1], false);
  paintScrim();
}

/** Opens a modal. With no id it opens the shared shell #stl-modal: openModal(null, { title, html }). */
export function openModal(id, options) {
  return openOverlay(id || IDS.modal, options);
}

/** Closes the modal with that id, or whichever modal is open. */
export function closeModal(id) {
  if (id) return closeOverlay(id);
  const entry = [...stack].reverse().find((item) => item.kind === 'modal');
  if (entry) closeEntry(entry);
}

/** Closes everything this file opened. */
export function closeAll() {
  [...stack].reverse().forEach((entry) => closeEntry(entry, { silent: true, keepFocus: true }));
  paintScrim();
  announce(null, false);
}

export const overlays = {
  open: openOverlay,
  close: closeOverlay,
  closeAll,
  isOpen: (id) => stack.some((entry) => entry.id === String(id).replace(/^#/, '')),
  openIds: () => stack.map((entry) => entry.id),
  /** true when ANYTHING is open: an stl overlay of any kind (modals included) or a theme dialog. The pop-up checks this before it opens. */
  anyOpen: () => stack.length > 0 || nativeDialogOpen(),
  isBusy,
  sync: syncOverlays,
};

/* ------------------------------------------------------------------ wishlist */

function readWish() {
  const raw = storage.get(KEYS.wish, []);
  return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string' && h) : [];
}

function paintWish(root) {
  const handles = readWish();
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('[data-stl-wish]').forEach((btn) => {
    const saved = handles.includes(btn.getAttribute('data-stl-wish'));
    btn.classList.toggle(ON, saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
  });
  const badge = byId(IDS.wishN);
  if (badge) {
    badge.textContent = String(handles.length);
    badge.classList.toggle(ON, handles.length > 0);
  }
  return handles;
}

function writeWish(handles, handle, saved) {
  storage.set(KEYS.wish, handles);
  paintWish(document);
  fire(EVENTS.wish, { handles: [...handles], count: handles.length, handle: handle || null, saved: saved === undefined ? null : saved });
}

/** A product handle as stored: a trimmed string. Anything else (null, an object, '') is ''. */
function asHandle(value) {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

export const wishlist = {
  /** The saved product handles, oldest first. A copy: change it through toggle, add or remove. */
  get: () => readWish(),
  has: (handle) => readWish().includes(asHandle(handle)),
  count: () => readWish().length,
  /** Saves or removes. Returns true when the product is now saved. Toasts unless { silent: true }. */
  toggle(rawHandle, options = {}) {
    const handle = asHandle(rawHandle);
    if (!handle) return false;
    const handles = readWish();
    const index = handles.indexOf(handle);
    const saved = index === -1;
    if (saved) handles.push(handle);
    else handles.splice(index, 1);
    writeWish(handles, handle, saved);
    if (!options.silent) toast(saved ? str('wish_saved', 'Saved to your wishlist') : str('wish_removed', 'Removed from your wishlist'));
    return saved;
  },
  add(rawHandle, options = {}) {
    const handle = asHandle(rawHandle);
    if (!handle) return false;
    if (!readWish().includes(handle)) wishlist.toggle(handle, options);
    return true;
  },
  remove(rawHandle, options = {}) {
    const handle = asHandle(rawHandle);
    if (handle && readWish().includes(handle)) wishlist.toggle(handle, { silent: true, ...options });
    return false;
  },
  /** Repaints every [data-stl-wish] button inside root (default: the document) and #stl-wishN. Call it after you inject cards. */
  sync: (root) => paintWish(root || document),
};

/* ------------------------------------------------------------------ cart */

let cartCount = null;

function paintBag(count) {
  const badge = byId(IDS.bagN);
  if (!badge) return;
  badge.textContent = String(count);
  badge.classList.toggle(ON, count > 0);
}

function setCount(count, detail) {
  const next = Number(count);
  if (!Number.isFinite(next)) return;
  cartCount = next;
  paintBag(next);
  fire(EVENTS.cart, { count: next, cart: null, variantId: null, quantity: null, source: 'theme', ...detail });
}

async function fetchCart() {
  const url = String(route('cart_url', '/cart')).replace(/\/$/, '') + '.js';
  const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error('cart ' + response.status);
  return response.json();
}

export const cart = {
  /** The number of items in the cart as last known. Never null after the first paint. */
  count: () => (cartCount === null ? Number(config().cartCount) || 0 : cartCount),

  /** Re-reads /cart.js, repaints #stl-bagN and fires stl:cart:change. Resolves to the cart, or null. */
  async refresh() {
    try {
      const data = await fetchCart();
      setCount(data.item_count, { cart: data, source: 'refresh' });
      return data;
    } catch (e) {
      return null;
    }
  },

  /**
   * Adds a variant through the theme's own cart route, exactly as the theme's product form does:
   * same endpoint, same section ids, same CartAddEvent. So the native cart drawer opens and the
   * native cart bubble updates as they do after a native add. Fires stl:cart:change.
   * options: { properties: {..}, open: false to leave the drawer shut, sourceId }
   * Resolves to { ok: true, cart, count } or { ok: false, message }. Never rejects.
   */
  async add(variantId, qty = 1, options = {}) {
    const id = String(variantId || '').replace(/\D/g, '');
    const quantity = Math.max(1, parseInt(qty, 10) || 1);
    if (!id) return { ok: false, message: 'No variant' };

    const body = new FormData();
    body.set('id', id);
    body.set('quantity', String(quantity));
    Object.entries(options.properties || {}).forEach(([key, value]) => body.set('properties[' + key + ']', String(value)));
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) {
      body.set('sections', [...new Set(sectionIds)].join(','));
      body.set('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(route('cart_add_url', '/cart/add.js'), {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        credentials: 'same-origin',
        body,
      });
      const data = await response.json();
      if (data && data.status) {
        const message = data.description || data.message || str('cart_error', 'Sorry, that could not be added.');
        toast(message);
        return { ok: false, message };
      }

      let fresh = null;
      try {
        fresh = await fetchCart();
      } catch (e) {
        fresh = null;
      }
      const count = fresh ? fresh.item_count : cart.count() + quantity;

      /* our own drawers get out of the way before the theme's cart drawer opens */
      closeDrawers({ keepFocus: true });

      cartCount = count;
      paintBag(count);
      /* open: false. The theme's drawer opens itself on this event while it carries auto-open, and
         it reads the attribute at the moment the event arrives, so take it off for the dispatch
         (which is synchronous) and put it straight back. */
      const drawers = Array.from(document.querySelectorAll('cart-drawer-component'));
      /* Unicorn Cart (24 Sep 2026): when its cart block is on the page, Unicorn's own drawer opens
         on every add (it watches the /cart/add call itself), so the theme's drawer must stay shut
         or two drawers open on top of each other. Its block prints #unicorn-cart-block. */
      const unicornBlock = document.getElementById('unicorn-cart-block');
      /* mounted, not merely present: Unicorn's cart is set per market and never mounts on /en-us */
      const unicornOn = !!(unicornBlock && unicornBlock.children.length);
      const wantOpen = options.open !== false && !unicornOn;
      const held = !wantOpen ? drawers.filter((el) => el.hasAttribute('auto-open')) : [];
      held.forEach((el) => el.removeAttribute('auto-open'));
      /* the theme's cart drawer, cart bubble and cart items all listen for this on document */
      try {
        document.dispatchEvent(
          new CartAddEvent(fresh || {}, options.sourceId || 'stl-core', {
            source: 'stl-core',
            itemCount: count,
            variantId: id,
            quantity,
            sections: data ? data.sections : undefined,
          })
        );
      } finally {
        held.forEach((el) => el.setAttribute('auto-open', ''));
      }
      if (wantOpen) {
        const drawer = drawers[0];
        if (drawer && !drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
      }
      return { ok: true, cart: fresh, count };
    } catch (error) {
      const message = str('cart_error', 'Sorry, that could not be added.');
      toast(message);
      return { ok: false, message };
    }
  },
};

/* ------------------------------------------------------------------ style data for any product */

const styleCache = new Map();
const STYLE_SECTION = 'stl-style-data';

export const style = {
  /**
   * The style data for ANY product handle, from any page: the same object
   * snippets/stl-style.liquid prints in mode 'data' (name, colourways in order, labels, hexes,
   * prices in the active market's currency, per-size availability, pooled rating).
   * /products/<handle>.js carries none of that, so this asks the Section Rendering API for
   * sections/stl-style-data.liquid in that product's context. One request per handle per page
   * view, shared by every caller. Resolves to null when the handle no longer resolves (a deleted
   * or unpublished product): skip it, never throw. The wishlist drawer and the cart's Pairs-with
   * row both read through here.
   */
  fetch(rawHandle) {
    const handle = asHandle(rawHandle);
    if (!handle) return Promise.resolve(null);
    if (styleCache.has(handle)) return styleCache.get(handle);
    const root = String(route('root_url', '/')).replace(/\/$/, '');
    const url = root + '/products/' + encodeURIComponent(handle) + '?section_id=' + STYLE_SECTION;
    const request = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('script[data-stl-style]');
        return el ? JSON.parse(el.textContent || 'null') : null;
      })
      .catch(() => null);
    styleCache.set(handle, request);
    return request;
  },
  /** The colourway of the product the data was asked for. */
  current(data) {
    if (!data || !Array.isArray(data.colourways)) return null;
    return data.colourways.find((c) => c.current) || data.colourways[0] || null;
  },
  /** The in-stock size with the most units (ties go to the earlier size), or null when sold out. */
  bestSize(colourway) {
    if (!colourway || !Array.isArray(colourway.sizes)) return null;
    return colourway.sizes.reduce((best, size) => (size.available && (!best || size.q > best.q) ? size : best), null);
  },
};

/* ------------------------------------------------------------------ wiring, once */

let booted = false;

function boot() {
  if (booted) return;
  booted = true;

  /* closed overlays cannot be tabbed into */
  document.querySelectorAll('[data-stl-overlay]').forEach((el) => {
    prepare(el);
    if (!el.classList.contains(ON)) el.setAttribute('inert', '');
  });

  delegate(document, 'click', '[data-stl-open]', async (event, el) => {
    const name = el.getAttribute('data-stl-open');
    const target = byId(name);
    if (!target) {
      /* a dead trigger must not be silent: two slices building in parallel find out here */
      console.warn('[stl-core] data-stl-open="' + name + '": no element with that id on this page.');
      return;
    }
    event.preventDefault();
    const dialog = topModalDialog();
    if (dialog && !dialog.contains(target)) {
      /* the trigger sits inside a theme dialog (say, the cart drawer): shut it first. The trigger
         is hidden once it has shut, so focus will go back to wherever the browser put it. */
      await closeThemeDialogs();
      openOverlay(target.id);
      return;
    }
    openOverlay(target.id, { returnTo: el });
  });

  delegate(document, 'click', '[data-stl-close]', (event, el) => {
    const owner = el.closest('[data-stl-overlay]');
    if (!owner) return;
    event.preventDefault();
    closeOverlay(owner.id);
  });

  delegate(document, 'click', '[data-stl-wish]', (event, el) => {
    event.preventDefault();
    wishlist.toggle(el.getAttribute('data-stl-wish'));
  });

  delegate(document, 'click', '[data-stl-add]', async (event, el) => {
    event.preventDefault();
    if (el.getAttribute('aria-busy') === 'true') return;
    el.setAttribute('aria-busy', 'true');
    await cart.add(el.getAttribute('data-stl-add'), el.getAttribute('data-stl-qty') || 1, { sourceId: el.id || 'stl-core' });
    el.removeAttribute('aria-busy');
  });

  delegate(document, 'click', '[data-stl-toast]', (event, el) => {
    toast(el.getAttribute('data-stl-toast'));
  });

  /* the scrim and a modal's own backdrop close what is above them */
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.id === IDS.scrim) closeDrawers();
    else if (target.matches('.stl-modal[data-stl-overlay]')) closeOverlay(target.id);
  });

  /* Escape closes the top overlay only; the theme's own dialogs handle their own Escape */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !stack.length || nativeDialogOpen()) return;
    event.preventDefault();
    closeOverlay();
  });

  /* the theme's cart changes (quantity, remove, a native add) keep #stl-bagN and our event in step */
  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    if (detail.data && detail.data.source === 'stl-core') {
      fire(EVENTS.cart, {
        count: cart.count(),
        cart: detail.resource && detail.resource.items ? detail.resource : null,
        variantId: detail.data.variantId || null,
        quantity: detail.data.quantity || null,
        source: 'add',
      });
      return;
    }
    const count = detail.resource && detail.resource.item_count;
    if (typeof count === 'number') setCount(count, { cart: detail.resource, source: 'theme' });
    else cart.refresh();
  });

  /* the theme's dialogs (cart drawer, search, quick add) count as busy. Their events do not bubble, so capture. */
  ['dialog:open', 'dialog:close'].forEach((name) => {
    document.addEventListener(
      name,
      (event) =>
        setTimeout(() => {
          /* a theme dialog opening does what a drawer opening does: an open drawer or menu closes
             (the mock's searchOpen and bag both did), so nothing of ours is left underneath it */
          const dialog = event.type === 'dialog:open' ? topModalDialog() : null;
          const under = dialog ? stack.filter((entry) => CLOSED_BY_A_DRAWER.includes(entry.kind) && !dialog.contains(entry.el)) : [];
          under.forEach((entry) => closeEntry(entry, { silent: true, keepFocus: true }));
          /* openIds changed even though busy did not, so this one is announced outright */
          if (under.length) announce(under[under.length - 1], false);
          else syncOverlays();
          /* a toast that is showing follows the top layer in and out */
          placeToast(byId(IDS.toast));
        }, 60),
      true
    );
  });

  /* ids another slice toggles by hand */
  if ('MutationObserver' in window) {
    const observer = new MutationObserver(() => syncOverlays());
    [IDS.stickyAtc, IDS.menu, IDS.pop].forEach((id) => {
      const el = byId(id);
      if (el && !el.hasAttribute('data-stl-overlay')) observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
    document.querySelectorAll('[data-stl-busy]').forEach((el) => observer.observe(el, { attributes: true, attributeFilter: ['class'] }));
  }

  /* another tab changed the wishlist */
  window.addEventListener('storage', (event) => {
    if (event.key === KEYS.wish) {
      const handles = paintWish(document);
      fire(EVENTS.wish, { handles, count: handles.length, handle: null, saved: null });
    }
  });

  /* a page restored from the back-forward cache can be stale */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      paintWish(document);
      cart.refresh();
    }
  });

  /* Unicorn Cart's drawer changes the cart with its own requests (remove, +, -) and tells nobody,
     so the bag count went stale: 1 item in the bag, 3 on the header (10 Oct 2026). Any finished
     request that writes the cart, whoever sent it, and the drawer closing re-read /cart.js. */
  let recount = 0;
  const askRecount = () => {
    clearTimeout(recount);
    recount = setTimeout(() => cart.refresh(), 250);
  };
  const WRITES_CART = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => WRITES_CART.test(new URL(entry.name, location.href).pathname))) askRecount();
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {
      /* no resource timing: the drawer-close re-read below still runs */
    }
  }
  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof Element && event.target.id === 'unicorn-cart-dialog') askRecount();
    },
    true
  );

  paintWish(document);
  paintBag(cart.count());
  lastBusy = isBusy();
  document.documentElement.toggleAttribute('data-stl-busy', lastBusy);
  document.documentElement.setAttribute('data-stl-core', 'ready');
}

onReady(boot);

export default {
  EVENTS,
  KEYS,
  IDS,
  config,
  onReady,
  delegate,
  storage,
  money,
  toast,
  trapFocus,
  openOverlay,
  closeOverlay,
  openDrawer,
  closeDrawers,
  openModal,
  closeModal,
  closeAll,
  isBusy,
  syncOverlays,
  overlays,
  wishlist,
  cart,
  style,
};
